import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  FileText,
  Download,
  Printer,
  Mail,
  Loader2,
} from 'lucide-react';
import { getCurrentBodyCorporateId, getCurrentProfileSafe, supabase, Account, Flat, Owner, Transaction } from '../lib/supabase';
import toast from 'react-hot-toast';
import { pdf, PDFDownloadLink } from '@react-pdf/renderer';
import StatementPDF from '../components/StatementPDF';
import { formatDate, getLocalDateString, getStatementDueDate } from '../lib/dateUtils';
import EmailService from '../services/EmailService';
import { buildStatementTemplateValues, renderStatementTemplate } from '../lib/statementTemplate';

interface AccountWithDetails extends Account {
  flat?: Flat;
  owners?: Owner[];
  balance?: number;
}

const Statements: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountIdFromUrl = searchParams.get('account');
  const [statementRole, setStatementRole] = useState('');

  const [accounts, setAccounts] = useState<AccountWithDetails[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>(accountIdFromUrl || '');
  const [generating, setGenerating] = useState(false);
  const [periodFrom, setPeriodFrom] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [periodTo, setPeriodTo] = useState(new Date().toISOString().split('T')[0]);
  const [statementData, setStatementData] = useState<any>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [bodyCorporate, setBodyCorporate] = useState<any>(null);
  const [bankAccount, setBankAccount] = useState<any>(null);
  const [statementSettings, setStatementSettings] = useState<any>(null);
  const templateValues = statementData ? buildStatementTemplateValues(statementData) : {};
  const templateText = (text: string | null | undefined) => renderStatementTemplate(text, templateValues);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      const profile = await getCurrentProfileSafe();
      setStatementRole(profile?.role || 'staff');

      const id = await getCurrentBodyCorporateId();
      await fetchAccounts(id);
      await fetchBodyCorporateDetails(id);
      await fetchStatementSettings(id);
    };

    load();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (accountIdFromUrl) {
      setSelectedAccountId(accountIdFromUrl);
      generateStatement(accountIdFromUrl);
    }
  }, [accountIdFromUrl]);

  const fetchAccounts = async (id: string | null) => {
    if (!id) {
      setAccounts([]);
      toast.error('No body corporate is configured for this user.');
      return;
    }

    try {
      const { data: accountsData, error: accountsError } = await supabase
        .from('accounts')
        .select('*')
        .eq('body_corporate_id', id)
        .eq('status', 'active');

      if (accountsError) throw accountsError;

      // Get flats and owners for each account
      const accountsWithDetails = await Promise.all(
        (accountsData || []).map(async (account: Account) => {
          const { data: flatData } = await supabase
            .from('flats')
            .select('*')
            .eq('id', account.flat_id)
            .single();

          const { data: accountOwnerData } = await supabase
            .from('account_owners')
            .select('owner_id')
            .eq('account_id', account.id)
            .eq('is_primary', true)
            .single();

          let owners: Owner[] = [];
          if (accountOwnerData) {
            const { data: ownerData } = await supabase
              .from('owners')
              .select('*')
              .eq('id', accountOwnerData.owner_id)
              .single();
            if (ownerData) owners = [ownerData];
          }

          return {
            ...account,
            flat: flatData || undefined,
            owners,
          };
        })
      );

      setAccounts(accountsWithDetails);
    } catch (error) {
      console.error(error);
      toast.error('Error fetching accounts');
    }
  };

  const fetchBodyCorporateDetails = async (id: string | null) => {
    if (!id) return;

    const { data, error } = await supabase
      .from('body_corporates')
      .select('*')
      .eq('id', id)
      .single();

    if (!error && data) {
      setBodyCorporate(data);

      // Fetch bank account
      const { data: bankData } = await supabase
        .from('bank_accounts')
        .select('*')
        .eq('body_corporate_id', id)
        .eq('is_primary', true)
        .single();

      if (bankData) setBankAccount(bankData);
    }
  };

  const fetchStatementSettings = async (id: string | null) => {
    if (!id) return;

    const { data, error } = await supabase
      .from('statement_settings')
      .select('*')
      .eq('body_corporate_id', id)
      .single();

    if (!error && data) {
      setStatementSettings(data);
    }
  };

  const generateStatement = async (accountId?: string) => {
    if (statementRole === 'viewer') {
      toast.error('Viewer accounts can only view statements and cannot generate anything.');
      return;
    }

    const id = accountId || selectedAccountId;
    if (!id) {
      toast.error('Please select an account');
      return;
    }

    setGenerating(true);
    setStatementData(null);

    try {
      // Get account details
      const account = accounts.find(a => a.id === id);
      if (!account) {
        toast.error('Account not found');
        setGenerating(false);
        return;
      }

      // Retrieve the ledger up to the statement end date.  A statement is a
      // period view of an account, so entries before the start date must form
      // the balance brought forward rather than being silently discarded.
      const { data: ledgerTransactions, error: txError } = await supabase
        .from('transactions')
        .select('*')
        .eq('account_id', id)
        .eq('status', 'posted')
        .lte('transaction_date', periodTo)
        .order('transaction_date', { ascending: true })
        .order('created_at', { ascending: true });

      if (txError) throw txError;

      const priorTransactions = (ledgerTransactions || []).filter(
        (transaction: Transaction) => transaction.transaction_date < periodFrom
      );
      const transactions = (ledgerTransactions || []).filter(
        (transaction: Transaction) => transaction.transaction_date >= periodFrom
      );
      const balanceBroughtForward = priorTransactions.reduce(
        (balance: number, transaction: Transaction) =>
          balance + Number(transaction.debit_amount || 0) - Number(transaction.credit_amount || 0),
        0
      );
      const dueDate = getStatementDueDate(transactions);

      // Every transaction in the statement uses the account's real running
      // balance, starting with the balance brought forward from prior periods.
      let runningBalance = balanceBroughtForward;
      const transactionsWithBalance = transactions.map((t: Transaction) => {
        runningBalance += (t.debit_amount || 0) - (t.credit_amount || 0);
        return {
          ...t,
          running_balance: runningBalance,
        };
      });

      // Calculate summary
      const summary = {
        opening_balance: balanceBroughtForward,
        total_levies: 0,
        total_special_levies: 0,
        total_other_charges: 0,
        total_penalties: 0,
        total_payments: 0,
        total_credits: 0,
        amount_due: 0,
      };

      transactionsWithBalance.forEach((t: Transaction & { balance: number }) => {
        switch (t.transaction_type) {
          case 'opening_balance':
            summary.opening_balance += (t.debit_amount || 0) - (t.credit_amount || 0);
            break;
          case 'monthly_levy':
          case 'quarterly_levy':
          case 'maintenance_levy':
            summary.total_levies += t.debit_amount || 0;
            break;
          case 'special_levy':
            summary.total_special_levies += t.debit_amount || 0;
            break;
          case 'administrative_charge':
          case 'repair_charge':
          case 'miscellaneous_charge':
          case 'other_charge':
            summary.total_other_charges += t.debit_amount || 0;
            break;
          case 'penalty':
          case 'interest':
            summary.total_penalties += t.debit_amount || 0;
            break;
          case 'payment':
            summary.total_payments += t.credit_amount || 0;
            break;
          case 'credit_adjustment':
            summary.total_credits += t.credit_amount || 0;
            break;
          case 'reversal':
            // A reversal must remain visible in the ledger and must be
            // reflected in the summary as well: reversing a receipt is a
            // debit, while reversing a charge is a credit.
            summary.total_other_charges += t.debit_amount || 0;
            summary.total_credits += t.credit_amount || 0;
            break;
        }
      });

      summary.amount_due = 
        summary.opening_balance +
        summary.total_levies +
        summary.total_special_levies +
        summary.total_other_charges +
        summary.total_penalties -
        summary.total_payments -
        summary.total_credits;

      setStatementData({
        account,
        transactions: transactionsWithBalance,
        summary,
        periodFrom,
        periodTo,
        generatedDate: new Date().toISOString(),
        bodyCorporate,
        bankAccount,
        statementSettings,
        dueDate,
      });

      toast.success('Statement generated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Error generating statement');
    }
    setGenerating(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const buildEmailMessage = () => {
    const owner = statementData?.account?.owners?.[0];
    return `Dear ${owner?.full_name || 'Owner'},

Please find your levy statement below.

Account Number: ${statementData.account.account_number}
Statement Period: ${formatDate(statementData.periodFrom)} - ${formatDate(statementData.periodTo)}
Amount Due: R ${statementData.summary.amount_due.toFixed(2)}

Kind regards,
${bodyCorporate?.name || 'Body Corporate'}`;
  };

  const openEmailEditor = () => {
    const owner = statementData?.account?.owners?.[0];
    if (!owner?.email) {
      toast.error('This owner has no email address on file');
      return;
    }
    setEmailMessage(buildEmailMessage());
    setShowEmailEditor(true);
  };

  const handleEmail = async () => {
    if (!statementData) {
      toast.error('Generate a statement before emailing it');
      return;
    }

    try {
      setSendingEmail(true);
      const service = new EmailService();
      const statementPdf = await pdf(<StatementPDF data={statementData} />).toBlob();
      const filename = `statement-${statementData.account.account_number}-${new Date().toISOString().split('T')[0]}.pdf`;
      const result = await service.sendStatementEmail(statementData.account.id, statementPdf, {
        customMessage: emailMessage.trim() || buildEmailMessage(),
        filename,
      });

      toast.success(`Statement emailed to ${result.to}`);
      setShowEmailEditor(false);
    } catch (error) {
      console.error('Error sending statement email:', error);
      toast.error(error instanceof Error ? error.message : 'Unable to send the statement email');
    } finally {
      setSendingEmail(false);
    }
  };

  const getAccountDisplay = (account: AccountWithDetails) => {
    const ownerName = account.owners?.[0]?.full_name || 'No owner';
    return `${account.account_number} - ${account.flat?.flat_number || ''} (${ownerName})`;
  };

  const applyPeriodPreset = (preset: 'monthly' | 'quarterly') => {
    const today = new Date();
    const startMonth = preset === 'monthly'
      ? today.getMonth()
      : Math.floor(today.getMonth() / 3) * 3;
    const startDate = new Date(today.getFullYear(), startMonth, 1);

    setPeriodFrom(getLocalDateString(startDate));
    setPeriodTo(getLocalDateString(today));
  };

  // Calculate total outstanding for all accounts
  const totalOutstanding = accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">Statements</h1>
          <p className="text-gray-500">Generate and manage levy statements</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>Total Outstanding:</span>
          <span className="font-bold text-red-600">R {totalOutstanding.toFixed(2)}</span>
        </div>
      </div>

      {/* Statement Generator */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold">Generate Statement</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Quick period:</span>
            <button
              type="button"
              onClick={() => applyPeriodPreset('monthly')}
              className="px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => applyPeriodPreset('quarterly')}
              className="px-3 py-1.5 text-sm font-medium text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
            >
              Quarterly
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Account Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account *
            </label>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select an account...</option>
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {getAccountDisplay(acc)}
                </option>
              ))}
            </select>
          </div>

          {/* From Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Generate Button */}
          <div className="flex items-end">
            <button
              onClick={() => generateStatement()}
              disabled={!selectedAccountId || generating || statementRole === 'viewer'}
              className="w-full btn-primary flex items-center justify-center gap-2 py-2.5"
            >
              {generating ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText size={18} />
                  Generate
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Statement Preview */}
      {statementData && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold">Statement Preview</h3>
              <p className="text-sm text-gray-500">
                {statementData.account.account_number} - {statementData.account.flat?.flat_number}
              </p>
            </div>
            <div className="flex gap-2">
              {statementRole !== 'viewer' && (
                <>
                  <PDFDownloadLink
                    document={<StatementPDF data={statementData} />}
                    fileName={`statement-${statementData.account.account_number}-${new Date().toISOString().split('T')[0]}.pdf`}
                  >
                    {({ loading }) => (
                      <button
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                        disabled={loading}
                      >
                        {loading ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Download size={18} />
                        )}
                        Download PDF
                      </button>
                    )}
                  </PDFDownloadLink>
                  <button
                    onClick={handlePrint}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <Printer size={18} />
                    Print
                  </button>
                  <button
                    onClick={openEmailEditor}
                    disabled={sendingEmail}
                    className="btn-secondary flex items-center gap-2 disabled:opacity-60"
                  >
                    {sendingEmail ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />}
                    {sendingEmail ? 'Sending...' : 'Email'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Statement Content */}
          <div className="bg-slate-50 p-4 sm:p-8" id="statement-content">
            <div className="mx-auto max-w-5xl overflow-hidden rounded-sm bg-white shadow-sm ring-1 ring-slate-200">
            {/* Header */}
            <div className="border-t-4 border-slate-900 px-6 pb-5 pt-7 sm:px-10">
              {bodyCorporate?.logo_path && (
                <img 
                  src={bodyCorporate.logo_path} 
                  alt="Logo" 
                  className="mb-3 h-14 max-w-[180px] object-contain"
                />
              )}
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                {bodyCorporate?.name || 'Body Corporate'}
              </h1>
              <p className="text-sm text-gray-600">
                {bodyCorporate?.physical_address}
              </p>
              <p className="text-sm text-gray-600">
                {bodyCorporate?.telephone} | {bodyCorporate?.email}
              </p>
              {bodyCorporate?.registration_number && (
                <p className="text-sm text-gray-600">Reg No: {bodyCorporate.registration_number}</p>
              )}
            </div>

            {/* Statement Title */}
            <div className="mb-6 rounded-lg bg-slate-900 px-5 py-4 text-center text-white">
              <h2 className="text-xl font-bold">
                {statementSettings?.statement_title || 'LEVY STATEMENT'}
              </h2>
              <p className="text-sm text-slate-300">
                Statement Date: {formatDate(statementData.generatedDate)}
              </p>
              <p className="text-sm text-slate-300">
                Period: {formatDate(statementData.periodFrom)} - {formatDate(statementData.periodTo)}
              </p>
            </div>

            {/* Owner & Account Details */}
            <div className="px-6 pb-8 sm:px-10">
            {statementSettings?.intro_text && <p className="mb-6 border-l-4 border-slate-900 pl-4 text-sm leading-6 text-slate-600">{templateText(statementSettings.intro_text)}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-500">Account Number</p>
                <p className="font-semibold">{statementData.account.account_number}</p>
                <p className="text-sm font-medium text-gray-500 mt-2">Unit/Section</p>
                <p className="font-semibold">{statementData.account.flat?.flat_number}</p>
                {statementData.account.flat?.physical_address && (
                  <>
                    <p className="text-sm font-medium text-gray-500 mt-2">Property Address</p>
                    <p className="font-semibold">{statementData.account.flat.physical_address}</p>
                  </>
                )}
                {statementData.account.flat?.participation_quota != null && (
                  <>
                    <p className="text-sm font-medium text-gray-500 mt-2">Participation Quota</p>
                    <p className="font-semibold">{statementData.account.flat.participation_quota}%</p>
                  </>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Owner</p>
                <p className="font-semibold">{statementData.account.owners?.[0]?.full_name || 'N/A'}</p>
                <p className="text-sm font-medium text-gray-500 mt-2">Email</p>
                <p className="font-semibold">{statementData.account.owners?.[0]?.email || 'N/A'}</p>
              </div>
            </div>

            {/* Account Summary */}
            <div className="mb-6">
              <h3 className="mb-3 border-b-2 border-slate-900 pb-2 text-sm font-bold uppercase tracking-wide text-slate-800">{templateText(statementSettings?.summary_heading || 'Account summary')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Opening Balance</p>
                  <p className="font-medium">R {statementData.summary.opening_balance.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Levies</p>
                  <p className="font-medium">R {statementData.summary.total_levies.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Special Levies</p>
                  <p className="font-medium">R {statementData.summary.total_special_levies.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Other Charges</p>
                  <p className="font-medium">R {statementData.summary.total_other_charges.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Penalties/Interest</p>
                  <p className="font-medium">R {statementData.summary.total_penalties.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Payments Received</p>
                  <p className="font-medium text-green-600">R {statementData.summary.total_payments.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Credits</p>
                  <p className="font-medium text-green-600">R {statementData.summary.total_credits.toFixed(2)}</p>
                </div>
                <div className="bg-slate-900 p-3 rounded-lg border-2 border-slate-900">
                  <p className="text-xs font-medium text-slate-200">AMOUNT DUE</p>
                  <p className="text-lg font-bold text-white">
                    R {statementData.summary.amount_due.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Transaction Table */}
            <div className="mb-6">
              <h3 className="mb-3 border-b-2 border-slate-900 pb-2 text-sm font-bold uppercase tracking-wide text-slate-800">{templateText(statementSettings?.transactions_heading || 'Transaction history')}</h3>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Date</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Description</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Reference</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Debit</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Credit</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {statementData.transactions.map((t: any) => (
                      <tr key={t.id}>
                        <td className="px-3 py-2">{formatDate(t.transaction_date)}</td>
                        <td className="px-3 py-2">{t.description}</td>
                        <td className="px-3 py-2 text-gray-500">{t.reference_number || '-'}</td>
                        <td className="px-3 py-2 text-right text-red-600">
                          {t.debit_amount > 0 ? `R ${t.debit_amount.toFixed(2)}` : '-'}
                        </td>
                        <td className="px-3 py-2 text-right text-green-600">
                          {t.credit_amount > 0 ? `R ${t.credit_amount.toFixed(2)}` : '-'}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          R {t.running_balance.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payment Details */}
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-800">{templateText(statementSettings?.payment_heading || 'Payment details')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <p><span className="text-gray-500">Bank:</span> {bankAccount?.bank_name || 'N/A'}</p>
                  <p><span className="text-gray-500">Account Name:</span> {bankAccount?.account_name || 'N/A'}</p>
                </div>
                <div>
                  <p><span className="text-gray-500">Account Number:</span> {bankAccount?.account_number || 'N/A'}</p>
                  <p><span className="text-gray-500">Branch Code:</span> {bankAccount?.branch_code || 'N/A'}</p>
                </div>
              </div>
              <div className="mt-2 text-sm">
                <p><span className="text-gray-500">Payment Reference:</span> <strong>{statementData.account.account_number}</strong></p>
                <p><span className="text-gray-500">Due Date:</span> <strong>{formatDate(statementData.dueDate || statementData.periodTo)}</strong></p>
                <p><span className="text-gray-500">Amount Due:</span> <strong className="text-red-600">R {statementData.summary.amount_due.toFixed(2)}</strong></p>
              </div>
              {statementSettings?.payment_instructions && (
                <p className="mt-3 text-sm text-gray-600">{templateText(statementSettings.payment_instructions)}</p>
              )}
            </div>

            {/* Notice */}
            {statementSettings?.notice_text && (
              <div className="text-sm text-gray-600 border-t border-gray-200 pt-4 mt-4">
                <p>{templateText(statementSettings.notice_text)}</p>
              </div>
            )}
            </div>
            </div>
          </div>
        </div>
      )}

      {showEmailEditor && statementData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold">Email statement</h3>
            <p className="mt-1 text-sm text-gray-500">
              Sending to {statementData.account.owners?.[0]?.email}. The PDF statement will be attached.
            </p>
            <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="statement-email-message">
              Message
            </label>
            <textarea
              id="statement-email-message"
              value={emailMessage}
              onChange={(event) => setEmailMessage(event.target.value)}
              rows={10}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowEmailEditor(false)}
                disabled={sendingEmail}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleEmail}
                disabled={sendingEmail}
                className="btn-primary disabled:opacity-60"
              >
                {sendingEmail ? 'Sending…' : 'Send statement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statements;
